import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import * as React from 'react';

import { cn } from '@/lib/utils';

type SidePanelSide = 'left' | 'right';

interface SidePanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  title?: React.ReactNode;
  description?: React.ReactNode;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  side?: SidePanelSide;
  width?: React.CSSProperties['width'];
  minWidth?: React.CSSProperties['minWidth'];
  maxWidth?: React.CSSProperties['maxWidth'];
  resizable?: boolean;
  resizeMinWidth?: number;
  resizeMaxWidth?: number;
  resizeStep?: number;
  resizeHandleAriaLabel?: string;
  closeOnOverlayClick?: boolean;
  showCloseButton?: boolean;
  className?: string;
  overlayClassName?: string;
  contentClassName?: string;
  headerClassName?: string;
  footerClassName?: string;
  closeButtonAriaLabel?: string;
}

const sideClasses: Record<SidePanelSide, string> = {
  left: 'left-0 border-r data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left',
  right: 'right-0 border-l data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right',
};

export const SidePanel: React.FC<SidePanelProps> = ({
  open,
  onOpenChange,
  children,
  title,
  description,
  header,
  footer,
  side = 'right',
  width = 'fit-content',
  minWidth = 'min(28rem, 100vw)',
  maxWidth = '75vw',
  resizable = false,
  resizeMinWidth,
  resizeMaxWidth,
  resizeStep = 24,
  resizeHandleAriaLabel = 'Resize side panel',
  closeOnOverlayClick = false,
  showCloseButton = true,
  className,
  overlayClassName,
  contentClassName,
  headerClassName,
  footerClassName,
  closeButtonAriaLabel = 'Close side panel',
}) => {
  const hasDefaultHeader = title || description;
  const contentRef = React.useRef<HTMLDivElement>(null);
  const dragStateRef = React.useRef<{
    startX: number;
    startWidth: number;
    previousCursor: string;
    previousUserSelect: string;
  } | null>(null);
  const [panelWidth, setPanelWidth] = React.useState<number | null>(null);

  const getResizeBounds = React.useCallback(() => {
    const viewportWidth = window.innerWidth || 0;
    const fallbackMin = Math.max(320, Math.round(viewportWidth * 0.25));
    const fallbackMax = Math.round(viewportWidth * 0.75);
    const min = resizeMinWidth ?? fallbackMin;
    const max = resizeMaxWidth ?? fallbackMax;

    return {
      min: Math.min(min, max),
      max: Math.max(min, max),
    };
  }, [resizeMaxWidth, resizeMinWidth]);

  const clampWidth = React.useCallback((value: number) => {
    const { min, max } = getResizeBounds();
    return Math.min(Math.max(value, min), max);
  }, [getResizeBounds]);

  React.useEffect(() => {
    if (!open) {
      setPanelWidth(null);
      const dragState = dragStateRef.current;
      if (dragState) {
        document.body.style.cursor = dragState.previousCursor;
        document.body.style.userSelect = dragState.previousUserSelect;
        dragStateRef.current = null;
      }
    }
  }, [open]);

  React.useEffect(() => {
    if (!resizable || !open || panelWidth === null) return undefined;

    const handleWindowResize = () => {
      setPanelWidth((currentWidth) => (
        currentWidth === null ? currentWidth : clampWidth(currentWidth)
      ));
    };

    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, [clampWidth, open, panelWidth, resizable]);

  React.useEffect(() => {
    if (!resizable) return undefined;

    const handlePointerMove = (event: PointerEvent) => {
      const dragState = dragStateRef.current;
      if (!dragState) return;

      const delta = side === 'right'
        ? dragState.startX - event.clientX
        : event.clientX - dragState.startX;
      setPanelWidth(clampWidth(dragState.startWidth + delta));
    };

    const stopResize = () => {
      const dragState = dragStateRef.current;
      if (!dragState) return;

      document.body.style.cursor = dragState.previousCursor;
      document.body.style.userSelect = dragState.previousUserSelect;
      dragStateRef.current = null;
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', stopResize);
    window.addEventListener('pointercancel', stopResize);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', stopResize);
      window.removeEventListener('pointercancel', stopResize);
      stopResize();
    };
  }, [clampWidth, resizable, side]);

  const startResize = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (!resizable || !contentRef.current) return;
    event.preventDefault();

    dragStateRef.current = {
      startX: event.clientX,
      startWidth: contentRef.current.getBoundingClientRect().width,
      previousCursor: document.body.style.cursor,
      previousUserSelect: document.body.style.userSelect,
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  const handleResizeKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (!resizable) return;

    const currentWidth = panelWidth ?? contentRef.current?.getBoundingClientRect().width;
    if (!currentWidth) return;

    const { min, max } = getResizeBounds();
    let nextWidth = currentWidth;

    if (event.key === 'Home') nextWidth = min;
    if (event.key === 'End') nextWidth = max;
    if (event.key === 'ArrowLeft') nextWidth = side === 'right' ? currentWidth + resizeStep : currentWidth - resizeStep;
    if (event.key === 'ArrowRight') nextWidth = side === 'right' ? currentWidth - resizeStep : currentWidth + resizeStep;

    if (nextWidth !== currentWidth) {
      event.preventDefault();
      setPanelWidth(clampWidth(nextWidth));
    }
  };

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-[100] bg-neutral-950/45 backdrop-blur-sm data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0',
            overlayClassName,
          )}
        />
        <DialogPrimitive.Content
          ref={contentRef}
          onInteractOutside={(event) => {
            if (!closeOnOverlayClick) {
              event.preventDefault();
            }
          }}
          style={{ width: panelWidth ?? width, minWidth, maxWidth }}
          className={cn(
            'fixed inset-y-0 z-[101] flex max-h-screen flex-col overflow-hidden bg-white shadow-[0_24px_70px_rgba(15,23,42,0.28)] outline-none transition ease-in-out data-[state=closed]:animate-out data-[state=closed]:duration-300 data-[state=open]:animate-in data-[state=open]:duration-300',
            sideClasses[side],
            className,
          )}
        >
          {resizable && (
            <button
              type="button"
              aria-label={resizeHandleAriaLabel}
              aria-orientation="vertical"
              onPointerDown={startResize}
              onKeyDown={handleResizeKeyDown}
              className={cn(
                'absolute top-0 z-30 flex h-full w-3 cursor-col-resize items-center justify-center bg-transparent outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
                side === 'right' ? 'left-0 -translate-x-1/2' : 'right-0 translate-x-1/2',
              )}
            >
              <span className="h-14 w-1 rounded-full bg-neutral-300/70 opacity-0 transition-opacity hover:opacity-100" />
            </button>
          )}

          {(header || hasDefaultHeader || showCloseButton) && (
            <div className={cn('flex items-start justify-between gap-4 border-b border-neutral-100 px-6 py-5', headerClassName)}>
              <div className="min-w-0 flex-1">
                {header || (
                  <>
                    {title && (
                      <DialogPrimitive.Title className="text-xl font-bold leading-none tracking-normal text-neutral-950">
                        {title}
                      </DialogPrimitive.Title>
                    )}
                    {description && (
                      <DialogPrimitive.Description className="mt-2 text-sm leading-5 text-neutral-600">
                        {description}
                      </DialogPrimitive.Description>
                    )}
                  </>
                )}
              </div>
              {showCloseButton && (
                <DialogPrimitive.Close
                  aria-label={closeButtonAriaLabel}
                  className="rounded-md p-1 text-neutral-600 transition-colors hover:bg-neutral-100 hover:text-neutral-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                >
                  <X className="h-5 w-5" />
                </DialogPrimitive.Close>
              )}
            </div>
          )}

          <div className={cn('min-h-0 flex-1 overflow-y-auto', contentClassName)}>
            {children}
          </div>

          {footer && (
            <div className={cn('border-t border-neutral-100 px-6 py-4', footerClassName)}>
              {footer}
            </div>
          )}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

export type { SidePanelProps, SidePanelSide };
